import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import ShieldIcon from "@/components/SVGComponents/ShieldIcon";
import { emailRegex, noLeadingSpaceRegex } from "@/lib/regex";
import { combineClasses, requiredMessage } from "@/lib/utils";
import { toOffsetDateTime, todayInputValue } from "@/lib/dateTime";
import { formatINR } from "@/lib/currency";
import toast from "@/lib/toast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getSubscriptionPlans } from "@/store/slices/subscriptionSlice";
import { getChapters } from "@/store/slices/chapterSlice";
import userService, { USER_CATEGORY_OPTIONS } from "@/services/api/user";
import type { UserCategory } from "@/services/api/user";
import type { UserType } from "@/types/question";
import styles from "./AddMemberModal.module.scss";

interface SelectOption {
  label: string;
  value: string;
}

const CHAPTERS_FETCH_PARAMS = { pageNo: 1, perPage: 100 };

const CATEGORY_OPTIONS: SelectOption[] = USER_CATEGORY_OPTIONS;
const SUBSCRIPTION_PLANS_FETCH_PARAMS = { pageNo: 1, perPage: 60 };

const TODAY_DATE_VALUE = todayInputValue();

const ROLE_OPTIONS: SelectOption[] = [
  { label: "Founder", value: "FOUNDER" },
  { label: "Investor", value: "INVESTOR" },
];

const MOBILE_NUMBER_REGEX = /^\d{10}$/;


const STATIC_GRID_FIELDS = {
  fullName: {
    controlType: "input",
    label: "Full name",
    labelVariant: "caption",
    placeholder: "e.g. Meher Anand",
    // Not `alphabetsRegex` (lib/regex.ts) — it only allows a single
    // unbroken word of letters, which would reject the placeholder's own
    // two-word example and any real hyphenated/apostrophe'd name.
    // `noLeadingSpaceRegex` just blocks a leading/space-only value.
    validations: {
      required: requiredMessage("Full name"),
      pattern: { value: noLeadingSpaceRegex, message: "Full name can't start with a space" },
      maxLength: { value: 100, message: "Full name must be under 100 characters" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
  workEmail: {
    controlType: "input",
    label: "Work email",
    labelVariant: "caption",
    placeholder: "name@company.com",
    validations: {
      required: requiredMessage("Work email"),
      pattern: { value: emailRegex, message: "Enter a valid email" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
  mobile: {
    controlType: "input",
    label: "Mobile",
    labelVariant: "caption",
    placeholder: "9876543210",
    validations: {
      required: requiredMessage("Mobile number"),
      pattern: { value: MOBILE_NUMBER_REGEX, message: "Enter a valid 10-digit mobile number" },
    },
    // `type: "number"` for real keystroke-level digit filtering — same
    // convention as every other numeric field in this codebase (e.g.
    // AddVenueModal's Capacity, CreatePlanModal's Seats included). Native
    // `maxLength` doesn't apply to number inputs, so the exact-10-digit
    // rule is enforced by `pattern` above, not truncated live.
    props: { type: "number", className: combineClasses(styles.fieldInput) },
  },
  starts: {
    controlType: "date",
    label: "Starts",
    labelVariant: "caption",
    isControlled: true,
    validations: { required: requiredMessage("Starts") },
    props: { className: styles.startsInput, min: TODAY_DATE_VALUE },
  },
  // Split from the old single "Company & role" free-text field, per the
  // user — company stays a plain string (no confirmed catalog to select
  // from), role becomes a real 2-option select instead of being typed.
  company: {
    controlType: "input",
    label: "Company",
    labelVariant: "caption",
    placeholder: "e.g. Studio Kind",
    // Left optional — no evidence (a real payload contract, an explicit
    // design note) that every member must have one on record.
    validations: { maxLength: { value: 100, message: "Company must be under 100 characters" } },
    props: { className: combineClasses(styles.fieldInput) },
  },
  role: {
    controlType: "select",
    label: "Role",
    labelVariant: "caption",
    isControlled: true,
    validations: { required: requiredMessage("Role") },
    props: { options: ROLE_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
  },
  category: {
    controlType: "select",
    label: "Category",
    labelVariant: "caption",
    placeholder: "Select category",
    isControlled: true,
    validations: { required: requiredMessage("Category") },
    props: { options: CATEGORY_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
  },
} satisfies FormConfig;

interface AddMemberFormValues {
  fullName: string;
  workEmail: string;
  mobile: string;
  chapter: SelectOption | null;
  membershipTier: SelectOption | null;
  starts: string;
  company: string;
  role: SelectOption | null;
  category: SelectOption | null;
}

export interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const AddMemberModal = ({ isOpen, onClose, onCreated }: AddMemberModalProps) => {
  const dispatch = useAppDispatch();
  const { plans, isFetching: isFetchingPlans } = useAppSelector((state) => state.subscription);
  const { chapters, isFetching: isFetchingChapters } = useAppSelector((state) => state.chapter);

  const { register, handleSubmit, control, watch, setValue, reset, formState } = useForm<AddMemberFormValues>({
    defaultValues: {
      fullName: "",
      workEmail: "",
      mobile: "",
      chapter: null,
      membershipTier: null,
      starts: TODAY_DATE_VALUE,
      company: "",
      role: ROLE_OPTIONS[0] ?? null,
      category: null,
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getChapters(CHAPTERS_FETCH_PARAMS));
  }, [dispatch, isOpen]);

  // Membership tiers depend on the selected category — refetch plans for it
  const selectedCategoryValue = watch("category")?.value as UserCategory | undefined;
  useEffect(() => {
    if (!isOpen) return;
    setValue("membershipTier", null);
    if (!selectedCategoryValue) return;
    dispatch(getSubscriptionPlans({ ...SUBSCRIPTION_PLANS_FETCH_PARAMS, category: selectedCategoryValue }));
  }, [dispatch, isOpen, selectedCategoryValue, setValue]);

  const membershipTierOptions: SelectOption[] = useMemo(
    () =>
      selectedCategoryValue
        ? plans?.map((plan) => {
            const durationLabel = plan.planDurationMonth === 1 ? "1 month" : `${plan.planDurationMonth} months`;
            return { label: `${plan.name} · ${durationLabel} · ${formatINR(plan.amount)}`, value: String(plan.id) };
          })
        : [],
    [plans, selectedCategoryValue],
  );

  const chapterOptions: SelectOption[] = useMemo(
    () => chapters.map((chapter) => ({ label: chapter.name, value: String(chapter.id) })),
    [chapters],
  );

  const selectedMembershipTier = watch("membershipTier");
  useEffect(() => {
    if (!selectedMembershipTier && !isFetchingPlans && membershipTierOptions.length > 0) {
      setValue("membershipTier", membershipTierOptions[0] ?? null);
    }
  }, [selectedMembershipTier, isFetchingPlans, membershipTierOptions, setValue]);

  const selectedChapter = watch("chapter");
  useEffect(() => {
    if (!selectedChapter && chapterOptions.length > 0) {
      setValue("chapter", chapterOptions[0] ?? null);
    }
  }, [selectedChapter, chapterOptions, setValue]);

  const gridFields: FormConfig = useMemo(
    () => ({
      fullName: STATIC_GRID_FIELDS.fullName,
      workEmail: STATIC_GRID_FIELDS.workEmail,
      mobile: STATIC_GRID_FIELDS.mobile,
      chapter: {
        controlType: "select",
        label: "Cities & Chapters",
        labelVariant: "caption",
        isControlled: true,
        validations: { required: requiredMessage("Chapter") },
        props: {
          options: chapterOptions,
          isSearchable: false,
          isLoading: isFetchingChapters && chapterOptions.length === 0,
          className: combineClasses(styles.fieldSelect),
        },
      },
      category: STATIC_GRID_FIELDS.category,
      membershipTier: {
        controlType: "select",
        label: "Membership tier",
        labelVariant: "caption",
        isControlled: true,
        validations: { required: requiredMessage("Membership tier") },
        isDisabled: !selectedCategoryValue,
        placeholder: selectedCategoryValue ? "Select membership tier" : "Select a category first",
        props: {
          options: membershipTierOptions,
          isSearchable: false,
          isLoading: isFetchingPlans,
          className: combineClasses(styles.fieldSelect, styles.membershipTierSelect),
        },
      },
      starts: STATIC_GRID_FIELDS.starts,
      company: STATIC_GRID_FIELDS.company,
      role: STATIC_GRID_FIELDS.role,
    }),
    [chapterOptions, isFetchingChapters, membershipTierOptions, isFetchingPlans, selectedCategoryValue],
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!values.fullName || !values.chapter || !values.membershipTier || !values.role || !values.category) return;
    const [firstName, ...rest] = values.fullName.trim().split(/\s+/);
    const lastName = rest.join(" ");
    try {
      await userService.createUser({
        firstName: firstName ?? "",
        lastName,
        email: values.workEmail,
        mobileNo: values.mobile,
        chapterId: Number(values.chapter.value),
        subscriptionPlanId: Number(values.membershipTier.value),
        startsAt: toOffsetDateTime(values.starts) ?? values.starts,
        ...(values.company ? { companyName: values.company } : {}),
        userType: values.role.value as UserType,
        userCategory: values.category.value as UserCategory,
      });
      toast.success("User Created Successfully");
      onCreated?.();
      handleClose();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add a member"
      subtitle="Creates the record and sends a welcome email with app access."
      onSubmit={onSubmit}
      submitLabel="Create member"
      submitIcon={<PlusIcon />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.addMemberModal)}
    >
      <div className={styles.fieldGrid}>
        {formRenderer<AddMemberFormValues>(gridFields, formState, register, control)}
      </div>

      <div className={styles.infoBanner}>
        <ShieldIcon className={styles.infoBannerIcon} />
        <p>Adding a member here bypasses the application questionnaire. The action is recorded in the audit log.</p>
      </div>
    </FormModal>
  );
};

export default AddMemberModal;
